// src/components/chat.jsx — FINAL ESDRAS IN-APP CHAT (real-time + premium UI)
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function Chat({ chatId, stylistId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isProvider, setIsProvider] = useState(false);
  const [clientData, setClientData] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();
  const messagesRef = useRef(null);

  // Identify the Client in the chat and check Provider role
  useEffect(() => {
    if (!user || !chatId) return;

    const initChatContext = async () => {
      try {
        // 1. Check if current user is a Stylist/Barber
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const role = userSnap.data().role;
          if (role === 'stylist' || role === 'barber' || role === 'admin') {
            setIsProvider(true);
          }
        }

        // 2. Get the other participant's data (the Client)
        const chatSnap = await getDoc(doc(db, 'chats', chatId));
        if (chatSnap.exists()) {
          const data = chatSnap.data();
          // Find the ID that isn't the stylistId (this is the client)
          const participants = data.participantIds || []; 
          const clientUid = participants.find(id => id !== stylistId);
          
          if (clientUid) {
            const clientSnap = await getDoc(doc(db, 'users', clientUid));
            if (clientSnap.exists()) {
              setClientData({ id: clientUid, ...clientSnap.data() });
            }
          }
        }
      } catch (err) {
        console.error("Chat initialization failed:", err);
      }
    };

    initChatContext();
  }, [user, chatId, stylistId]);

  // Real-time Message Listener
  useEffect(() => {
    if (!chatId) return;
    const q = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsub;
  }, [chatId]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        content: newMessage.trim(),
        senderId: user.uid,
        senderName: user.displayName || 'User',
        recipientId: isProvider ? clientData?.id : stylistId,
        createdAt: serverTimestamp(),
      });
      setNewMessage('');
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
    }
  };

  /**
   * INTEGRATED 3D CRANIAL PROJECTION LOGIC
   * This sends the stylist to the viewer but locks the view 
   * to the Client's unique 3D Head Scan (Cranial Solid).
   */
  const handleOpenViewer = () => {
    if (!clientData?.id) return;
    
    // We navigate to the ThreeViewer but pass the client's data in the state
    navigate(`/viewer/consultation`, { 
      state: { 
        targetClientUid: clientData.id,
        mode: 'cranial_solid_view',
        clientName: clientData.displayName || 'Client'
      } 
    });
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div style={chatContainerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center' }}>
            {clientData?.photoURL ? (
              <img src={clientData.photoURL} style={miniAvatar} alt="client" />
            ) : (
              <div style={{...miniAvatar, background: GOLD, display: 'grid', placeItems: 'center', color: 'black', fontWeight: 'bold'}}>
                {clientData?.displayName?.charAt(0) || 'C'}
              </div>
            )}
            <div style={{textAlign: 'left'}}>
              <h2 style={titleStyle}>
                {isProvider ? `${clientData?.displayName || 'Client'}` : 'Consultation Chat'}
              </h2>
              {isProvider && <p style={biometricLabel}>● BIOMETRIC SESSION LIVE</p>}
            </div>
          </div>

          {isProvider && (
            <button 
              onClick={handleOpenViewer} 
              style={launchBtnStyle}
              title="Project Client's 3D Cranial Solid"
            >
              LAUNCH 3D CRANIAL PROJECTION
            </button>
          )}
          
          <button onClick={onClose} style={closeX}>✕</button>
        </div>

        {/* Messages List */}
        <div ref={messagesRef} style={messageListStyle}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', opacity: 0.3, marginTop: '20%' }}>
              <img src="/icons/esdras-logo.png" style={{width: '60px', filter: 'grayscale(1)', marginBottom: '10px'}} alt="" />
              <p style={{fontSize: '0.8rem', letterSpacing: '1px'}}>SECURE ENCRYPTED CHANNEL OPENED</p>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                textAlign: msg.senderId === user.uid ? 'right' : 'left',
                marginBottom: '1.2rem',
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  padding: '1rem 1.4rem',
                  borderRadius: msg.senderId === user.uid ? '22px 22px 4px 22px' : '22px 22px 22px 4px',
                  background: msg.senderId === user.uid ? GOLD : 'rgba(255,255,255,0.08)',
                  color: msg.senderId === user.uid ? 'black' : 'white',
                  maxWidth: '85%',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                }}
              >
                <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5', fontWeight: '500' }}>{msg.content}</p>
              </div>
              <p style={timestampStyle}>
                {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Sending...'}
              </p>
            </div>
          ))}
        </div>

        {/* Input Area */}
        <div style={inputWrapperStyle}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Discuss hair goals or health alerts..."
            style={inputStyle}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
            style={{
                ...sendBtnStyle,
                opacity: (sending || !newMessage.trim()) ? 0.5 : 1
            }}
          >
            {sending ? '...' : 'SEND'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- STYLES ---
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.94)', display: 'grid', placeItems: 'center', zIndex: 9999, padding: '1rem', backdropFilter: 'blur(12px)' };
const chatContainerStyle = { background: NAVY, color: 'white', padding: '2rem', borderRadius: '35px', border: `2px solid ${GOLD}`, maxWidth: '550px', width: '100%', height: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 100px rgba(0,0,0,0.8)', position: 'relative' };
const headerStyle = { textAlign: 'center', marginBottom: '1.5rem', borderBottom: '1px solid rgba(184,134,11,0.2)', paddingBottom: '1.5rem' };
const titleStyle = { color: GOLD, fontSize: '1.4rem', fontWeight: '900', margin: 0, letterSpacing: '-0.5px' };
const biometricLabel = { fontSize: '0.6rem', color: '#2ecc71', fontWeight: 'bold', letterSpacing: '2px', margin: '5px 0 0' };
const launchBtnStyle = { marginTop: '15px', background: GOLD, border: `1px solid ${GOLD}`, color: 'black', padding: '12px 24px', borderRadius: '14px', fontSize: '0.75rem', fontWeight: '900', cursor: 'pointer', letterSpacing: '1px', transition: '0.3s', boxShadow: '0 4px 15px rgba(184, 134, 11, 0.4)' };
const closeX = { position: 'absolute', top: '25px', right: '25px', background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer', opacity: 0.5 };
const miniAvatar = { width: '45px', height: '45px', borderRadius: '50%', border: `2px solid ${GOLD}`, objectFit: 'cover' };
const messageListStyle = { flex: 1, overflowY: 'auto', marginBottom: '1.5rem', padding: '1rem', background: 'rgba(0,0,0,0.3)', borderRadius: '24px', scrollBehavior: 'smooth' };
const timestampStyle = { opacity: 0.4, fontSize: '0.65rem', margin: '5px 8px' };
const inputWrapperStyle = { display: 'flex', gap: '0.8rem', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50px', border: '1px solid rgba(255,255,255,0.1)' };
const inputStyle = { flex: 1, padding: '12px 20px', background: 'none', border: 'none', color: 'white', fontSize: '0.95rem', outline: 'none' };
const sendBtnStyle = { background: GOLD, color: 'black', padding: '0 25px', borderRadius: '50px', border: 'none', fontWeight: '900', cursor: 'pointer', fontSize: '0.8rem', transition: '0.3s' };
